import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function PaginationBar({ page, totalPages, total, perPage, onPage }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2);
  const end   = Math.min(totalPages, page + 2);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return (
    <div className="pagination" data-testid="maicpp-pagination">
      <span className="pagination-info">
        {((page - 1) * perPage + 1).toLocaleString()}–{Math.min(page * perPage, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => (
        <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>
      ))}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export default function MaicppDomainsPage() {
  const navigate = useNavigate();
  const [data,       setData]       = useState([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page,       setPage]       = useState(1);
  const [perPage,    setPerPage]    = useState(50);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [applied,    setApplied]    = useState('');

  const fetchData = useCallback(async (p, q, pp) => {
    setLoading(true);
    try {
      const params = { page: p, per_page: pp };
      if (q) params.q = q;
      const r = await axios.get(`${API}/api/partners/maicpp-domains`, { params, withCredentials: true });
      setData(r.data.data);
      setTotal(r.data.total);
      setTotalPages(r.data.total_pages);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(1, '', 50); }, []); // eslint-disable-line

  const handleSearch   = () => { setApplied(search); fetchData(1, search, perPage); };
  const handleReset    = () => { setSearch(''); setApplied(''); fetchData(1, '', perPage); };
  const handlePageSize = (pp) => { const n = Number(pp); setPerPage(n); fetchData(1, applied, n); };

  return (
    <div data-testid="maicpp-domains-page">
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        Dashboard &gt;{' '}
        <span style={{ cursor: 'pointer', color: '#3182ce' }} onClick={() => navigate('/partners')} data-testid="maicpp-back-link">
          Partner Dashboard
        </span>{' '}
        &gt; MAICPP Domains
      </div>

      <div className="page-header">
        <h1>MAICPP Domains</h1>
        <p>
          {total > 0
            ? `${total.toLocaleString()} registered MAICPP partner domains`
            : 'Loading MAICPP partner domains…'}
        </p>
      </div>

      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group" style={{ flex: 3 }}>
          <label className="filter-label">Search domain</label>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              className="filter-input"
              style={{ paddingLeft: 30 }}
              placeholder="Search by domain…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              data-testid="maicpp-search-input"
            />
          </div>
        </div>
        <div className="filter-group">
          <label className="filter-label">Page size</label>
          <select
            className="filter-input"
            value={perPage}
            onChange={e => handlePageSize(e.target.value)}
            data-testid="maicpp-page-size-select"
            style={{ width: 80 }}
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary"   onClick={handleSearch} data-testid="maicpp-search-btn">Search</button>
          <button className="btn btn-secondary" onClick={handleReset}  data-testid="maicpp-reset-btn">Reset</button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title" style={{ fontFamily: 'monospace' }}>maicpp_domains</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} rows)</span>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : data.length === 0 ? (
          <div className="empty-state">
            <h3>No rows found</h3>
            <p>{applied ? 'Try a different search term' : 'Table appears to be empty'}</p>
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table data-testid="maicpp-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>DOMAIN</th>
                    <th>ADDED</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} data-testid={`maicpp-row-${i}`}>
                      <td style={{ color: '#718096', fontSize: 12 }}>{row.id}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.domain}</td>
                      <td style={{ fontSize: 11, color: '#718096', whiteSpace: 'nowrap' }}>{formatDate(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} total={total} perPage={perPage} onPage={p => fetchData(p, applied, perPage)} />
          </>
        )}
      </div>
    </div>
  );
}
